import { toJS } from 'mobx';

/**
 * Deep-clone a value, tolerating MobX observable proxies that
 * `structuredClone` cannot handle.
 *
 * MobX `toJS()` does NOT recurse into non-observables — if a plain subtree
 * was assigned into a deeply-observable object, `toJS` skips it even when it
 * contains nested proxies. This leaves proxy objects that `structuredClone`
 * cannot clone (`DataCloneError`).
 *
 * Strategy:
 * 1. Fast path — `structuredClone(value)`: works for plain data.
 * 2. Medium path — `structuredClone(toJS(value))`: strips top-level proxies.
 * 3. Slow path — Recursive walk applying `toJS()` at each node to unwrap
 *    any nested proxies that the top-level `toJS()` skipped.
 */
export function deepCloneSafe<T>(value: T): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  // Fast path: value is already plain data
  try {
    return structuredClone(value);
  } catch {
    // contains something non-cloneable (likely MobX proxies)
  }

  // Medium path: toJS strips top-level observables, then structuredClone
  const unwrapped = toJS(value);
  try {
    return structuredClone(unwrapped);
  } catch {
    // toJS didn't recurse deep enough — nested proxies remain
  }

  // Slow path: recursive manual walk with toJS at each level
  return recursiveClone(unwrapped) as T;
}

function recursiveClone(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  if (value instanceof Map) {
    const result = new Map();
    for (const [k, v] of value.entries()) {
      result.set(recursiveClone(k), recursiveClone(toJS(v)));
    }
    return result;
  }

  if (value instanceof Set) {
    const result = new Set();
    for (const v of value.values()) {
      result.add(recursiveClone(toJS(v)));
    }
    return result;
  }

  if (Array.isArray(value)) {
    return value.map(item => recursiveClone(toJS(item)));
  }

  // Plain object
  const result: Record<string, unknown> = {};
  for (const key in value) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      result[key] = recursiveClone(
        toJS((value as Record<string, unknown>)[key]),
      );
    }
  }
  return result;
}
